import { useState, useEffect } from "react";
import API from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Activity, TrendingDown, TrendingUp, Flame, AlertTriangle, Calendar, Award, Heart, Lightbulb, Copy, ChevronDown, ChevronUp, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";

const SEVERITY_META = {
  severe: { cls: "bg-rose-100 text-rose-800 border-rose-200", emoji: "🔴" },
  moderate: { cls: "bg-amber-100 text-amber-800 border-amber-200", emoji: "🟡" },
  mild: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", emoji: "🟢" },
};

const CHIP_LABELS = {
  nausea: "Nausea", fatigue: "Fatigue", gi_upset: "GI upset", headache: "Headache",
  dizziness: "Dizziness", injection_site_reaction: "Injection site", decreased_appetite: "↓ appetite",
  constipation: "Constipation", diarrhea: "Diarrhea", other: "Other",
};

/**
 * Staff-facing wellness panel — embedded inside PatientDetailModal.
 *
 * Single-glance view of a patient's GLP-1 journey:
 *  - 12-week weight trend chart with first-vs-current delta callout
 *  - Streak status (current weeks toward $10 reward)
 *  - Side-effects logged in the last 30 days, severity-graded
 *  - Severe-event count up top so prescribers can spot dose-tolerance issues
 *
 * Backed by GET /api/wellness/admin/{patient_id}/summary which returns
 * everything in one round-trip. Permitted roles: admin, prescriber,
 * customer_service, it, super_admin.
 */
export default function PatientWellnessPanel({ patientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true); setErr(null);
    API.get(`/wellness/admin/${patientId}/summary`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load wellness summary"))
      .finally(() => setLoading(false));
  }, [patientId]);

  if (loading) return <div className="bg-slate-50 border border-slate-100 rounded-xl h-64 animate-pulse" data-testid="wellness-panel-loading" />;
  if (err) return <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 text-sm text-rose-700">{err}</div>;
  if (!data) return null;

  const hasWeights = (data.weight_logs_count || 0) > 0;
  const hasSideEffects = (data.side_effects_recent || []).length > 0;
  const lostWeight = data.weight_delta !== null && data.weight_delta < 0;
  const gainedWeight = data.weight_delta !== null && data.weight_delta > 0;
  const streakPct = data.streak_target ? Math.min(100, (data.current_streak / data.streak_target) * 100) : 0;

  const handleStaffUnlock = async () => {
    const reason = window.prompt(
      "Reason for manual unlock (saved to audit log):",
      "Patient request"
    );
    if (reason === null) return; // cancelled
    try {
      await API.post(`/wellness/admin/${patientId}/unlock-dow`, { reason });
      toast.success("Weigh-in day unlocked. Streak preserved.");
      const { data: refreshed } = await API.get(`/wellness/admin/${patientId}/summary`);
      setData(refreshed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unlock");
    }
  };

  return (
    <div className="space-y-4" data-testid="patient-wellness-panel">
      {/* Read-only clinical suggestions — only renders if the data triggers a rule */}
      {(data.suggestions || []).length > 0 && (
        <SuggestionsBadge suggestions={data.suggestions} />
      )}

      {/* Top stat row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat
          icon={Activity}
          label="Weigh-in day"
          value={data.weigh_in_dow_label || "Not set"}
          sublabel={
            data.weigh_in_dow_label
              ? (data.dow_locked ? "🔒 locked · staff can unlock" : "weekly cadence")
              : "patient hasn't picked one"
          }
          tone="purple"
          testid="wellness-panel-stat-dow"
        />
        <Stat
          icon={Flame}
          label={`Streak ${data.current_streak}/${data.streak_target}`}
          value={data.current_streak > 0 ? `${data.current_streak} wks` : "—"}
          sublabel={`Longest: ${data.longest_streak || 0}`}
          tone={data.current_streak > 0 ? "orange" : "slate"}
          testid="wellness-panel-stat-streak"
        />
        <Stat
          icon={Award}
          label="Coupons earned"
          value={data.coupons_earned_total || 0}
          sublabel="$10 stackable rewards"
          tone={data.coupons_earned_total > 0 ? "purple" : "slate"}
          testid="wellness-panel-stat-coupons"
        />
        <Stat
          icon={AlertTriangle}
          label="Severe symptoms (30d)"
          value={data.side_effects_severe_count || 0}
          sublabel={data.side_effects_severe_count > 0 ? "review needed" : "none reported"}
          tone={data.side_effects_severe_count > 0 ? "rose" : "slate"}
          testid="wellness-panel-stat-severe"
        />
      </div>

      {/* Staff manual unlock — appears only when the patient's weigh-in
          day is currently locked. Preserves their streak; audit-logged. */}
      {data.dow_locked && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5" data-testid="wellness-panel-locked-banner">
          <div className="flex items-center gap-2 text-xs text-amber-800">
            <Lock className="w-3.5 h-3.5 flex-shrink-0" />
            <span><strong>Locked</strong> to {data.weigh_in_dow_label}. Patient cannot change without breaking streak.</span>
          </div>
          <button
            type="button"
            onClick={handleStaffUnlock}
            className="inline-flex items-center gap-1 text-xs font-semibold rounded-full bg-white border border-amber-300 hover:bg-amber-100 px-3 py-1 text-amber-800 flex-shrink-0"
            data-testid="wellness-panel-staff-unlock"
          >
            <Unlock className="w-3.5 h-3.5" /> Unlock
          </button>
        </div>
      )}

      {/* Streak progress bar */}
      {data.weigh_in_dow_label && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-3" data-testid="wellness-panel-streak-bar">
          <div className="flex items-center justify-between mb-1.5 text-xs">
            <span className="font-medium text-slate-700 dark:text-slate-200">Current weigh-in streak</span>
            <span className="font-mono text-slate-500">{data.current_streak} / {data.streak_target}</span>
          </div>
          <div className="w-full bg-white/60 rounded-full h-2 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500" style={{ width: `${streakPct}%` }} />
          </div>
          {data.last_missed_dow_date && (
            <p className="text-[10px] text-amber-700 mt-1.5 inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Last missed: {data.last_missed_dow_date}
            </p>
          )}
        </div>
      )}

      {/* Weight trend */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4" data-testid="wellness-panel-weight-section">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h4 className="text-sm font-bold text-slate-900 dark:text-white">12-week weight trend</h4>
          {hasWeights && data.weight_delta !== null && (
            <div className={`inline-flex items-center gap-1 text-xs font-bold rounded-full px-2.5 py-0.5 border ${lostWeight ? "bg-emerald-50 text-emerald-700 border-emerald-200" : gainedWeight ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200"}`} data-testid="wellness-panel-weight-delta">
              {lostWeight ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
              {data.weight_delta > 0 ? "+" : ""}{data.weight_delta} lbs over 12 wks
            </div>
          )}
        </div>
        {hasWeights ? (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
              <div className="bg-slate-50 rounded-lg p-2 text-center">
                <p className="text-slate-500">Starting</p>
                <p className="font-bold text-slate-900">{data.first_weight} lbs</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-2 text-center">
                <p className="text-slate-500">Current</p>
                <p className="font-bold text-slate-900">{data.last_weight} lbs</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-2 text-center">
                <p className="text-slate-500">Logs</p>
                <p className="font-bold text-slate-900">{data.weight_logs_count}</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={data.weight_chart_points} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="logged_date" tick={{ fontSize: 10 }} />
                <YAxis dataKey="weight_lbs" tick={{ fontSize: 10 }} domain={["auto", "auto"]} width={40} />
                <RechartsTooltip
                  contentStyle={{ borderRadius: 8, fontSize: 12 }}
                  labelFormatter={(v) => `Date: ${v}`}
                  formatter={(v) => [`${v} lbs`, "Weight"]}
                />
                <ReferenceLine y={data.first_weight} stroke="#a78bfa" strokeDasharray="4 4" label={{ value: "Start", position: "left", fontSize: 9, fill: "#7c3aed" }} />
                <Line type="monotone" dataKey="weight_lbs" stroke="#7c3aed" strokeWidth={2.5} dot={{ r: 3, fill: "#7c3aed" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </>
        ) : (
          <p className="text-xs text-slate-500 text-center py-6">No weight logs yet.</p>
        )}
      </div>

      {/* Side effects (last 30 days) */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4" data-testid="wellness-panel-side-effects">
        <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-2 inline-flex items-center gap-1.5">
          <Heart className="w-3.5 h-3.5 text-rose-500" /> Side effects — last 30 days
        </h4>
        {hasSideEffects ? (
          <ul className="space-y-2 max-h-64 overflow-y-auto">
            {data.side_effects_recent.map((s) => {
              const meta = SEVERITY_META[s.severity] || SEVERITY_META.mild;
              return (
                <li key={s.id} className={`text-xs rounded-lg p-2.5 border ${meta.cls}`} data-testid={`wellness-panel-side-effect-${s.severity}`}>
                  <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                    <span className="inline-flex items-center gap-1.5 font-bold uppercase tracking-wider">
                      <span>{meta.emoji}</span> {s.severity}
                    </span>
                    <span className="text-[10px] opacity-75 font-mono">{s.logged_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-1">
                    {(s.chips || []).map((c) => (
                      <span key={c} className="bg-white/60 rounded-full px-1.5 py-0.5 text-[10px] font-medium">{CHIP_LABELS[c] || c}</span>
                    ))}
                  </div>
                  {s.notes && <p className="italic text-[11px] mt-1">&quot;{s.notes}&quot;</p>}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-xs text-slate-500 text-center py-6">No side effects logged in the last 30 days.</p>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sublabel, tone, testid }) {
  const tones = {
    purple: "bg-purple-50 border-purple-100 text-purple-900",
    orange: "bg-orange-50 border-orange-100 text-orange-900",
    rose: "bg-rose-50 border-rose-100 text-rose-900",
    slate: "bg-slate-50 border-slate-100 text-slate-700",
  };
  const iconColors = { purple: "text-purple-600", orange: "text-orange-500", rose: "text-rose-600", slate: "text-slate-400" };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone] || tones.slate}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 ${iconColors[tone] || iconColors.slate}`} />
        <span className="text-[10px] font-bold uppercase tracking-wider opacity-75">{label}</span>
      </div>
      <p className="text-lg font-bold leading-tight">{value}</p>
      <p className="text-[10px] opacity-60 truncate">{sublabel}</p>
    </div>
  );
}

const SUGGESTION_TONES = {
  info: { cls: "bg-emerald-50 border-emerald-200 text-emerald-900", icon: "text-emerald-600", emoji: "💡" },
  warn: { cls: "bg-amber-50 border-amber-200 text-amber-900", icon: "text-amber-600", emoji: "⚠️" },
  alert: { cls: "bg-rose-50 border-rose-200 text-rose-900", icon: "text-rose-600", emoji: "🚨" },
};

/**
 * Read-only clinical suggestion badge.
 * Surfaces objective patterns (plateau / tolerance / rapid loss) as hints —
 * never auto-acts, never edits Rx. The prescriber decides; this is a
 * heads-up so they don't miss a pattern that's hiding in the data.
 *
 * Each card has:
 *  - Copy note button → drops a pre-filled chart note into the clipboard
 *  - Apply to Rx button (when a pending order exists + matching template found)
 *    → fires `glpflo:apply-rx-suggestion` event so PrescriberDashboard can
 *      open that order with the template pre-selected. Pure UI shortcut into
 *      existing Rx template flow, no new backend logic.
 */
function SuggestionsBadge({ suggestions }) {
  const [open, setOpen] = useState(true);
  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Suggested note copied");
  };
  const applyToRx = (s) => {
    const tpl = s.suggested_rx_template;
    const order = s.pending_order;
    if (!tpl) {
      toast.info("No matching Rx template available for this suggestion.");
      return;
    }
    if (!order) {
      copy(s.suggested_note);
      toast.info("No pending order — chart note copied for next visit.");
      return;
    }
    // Fire global event — PrescriberDashboard listens and opens the order
    // review modal with the template pre-applied + suggestion note prepended
    // to rx_notes.
    window.dispatchEvent(new CustomEvent("glpflo:apply-rx-suggestion", {
      detail: {
        order_id: order.id,
        template: tpl,
        suggestion_note: s.suggested_note,
        suggestion_title: s.title,
      },
    }));
    toast.success(`Opening order with "${tpl.name}" pre-loaded`);
  };
  const alertCount = suggestions.filter((s) => s.severity === "alert").length;
  const warnCount = suggestions.filter((s) => s.severity === "warn").length;

  return (
    <div className="rounded-xl border-2 border-dashed border-purple-300 dark:border-purple-700/40 bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/10 dark:to-slate-900 p-3" data-testid="wellness-suggestions">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid="wellness-suggestions-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Lightbulb className="w-4 h-4 text-purple-600 flex-shrink-0" />
          <span className="text-sm font-bold text-slate-900 dark:text-white">
            {suggestions.length} clinical suggestion{suggestions.length === 1 ? "" : "s"}
          </span>
          <span className="hidden sm:inline text-[10px] text-slate-500">
            {alertCount > 0 && <span className="text-rose-600 font-bold mr-1.5">🚨 {alertCount} alert</span>}
            {warnCount > 0 && <span className="text-amber-600 font-bold mr-1.5">⚠️ {warnCount} warn</span>}
            <span>· read-only · prescriber decides</span>
          </span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <ul className="mt-3 space-y-2">
          {suggestions.map((s) => {
            const tone = SUGGESTION_TONES[s.severity] || SUGGESTION_TONES.info;
            const tpl = s.suggested_rx_template;
            const hasOrder = !!s.pending_order;
            return (
              <li key={s.id} className={`rounded-lg border p-3 ${tone.cls}`} data-testid={`wellness-suggestion-${s.id}`}>
                <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
                  <h5 className="text-sm font-bold inline-flex items-center gap-1.5">
                    <span>{tone.emoji}</span> {s.title}
                  </h5>
                  <div className="flex items-center gap-2 shrink-0">
                    {tpl && (
                      <button
                        type="button"
                        onClick={() => applyToRx(s)}
                        className={`text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${hasOrder ? "bg-white text-purple-700 border border-purple-300 hover:bg-purple-50" : "bg-white/60 text-slate-600 border border-slate-300 hover:bg-white"}`}
                        data-testid={`wellness-suggestion-apply-${s.id}`}
                        title={hasOrder ? `Open pending order with ${tpl.name} pre-loaded` : "No pending order — copy note for next visit"}
                      >
                        💊 {hasOrder ? `Apply ${tpl.name}` : `Copy ${tpl.name}`}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => copy(s.suggested_note)}
                      className="text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1 hover:underline shrink-0"
                      data-testid={`wellness-suggestion-copy-${s.id}`}
                      title="Copy suggested note to clipboard"
                    >
                      <Copy className="w-3 h-3" /> Copy note
                    </button>
                  </div>
                </div>
                <p className="text-xs opacity-90">{s.rationale}</p>
                {tpl && (
                  <div className="mt-1.5 text-[11px] opacity-80 inline-flex items-center gap-1.5 bg-white/50 dark:bg-slate-900/30 rounded px-2 py-0.5">
                    <span className="font-bold">Suggested template:</span>
                    <span className="font-mono">{tpl.name}</span>
                    <span className="opacity-60">· {tpl.dosage}</span>
                  </div>
                )}
                <details className="mt-2">
                  <summary className="text-[10px] uppercase tracking-wider font-bold opacity-60 cursor-pointer hover:opacity-100">
                    Suggested chart note
                  </summary>
                  <p className="text-[11px] italic mt-1.5 bg-white/60 dark:bg-slate-900/40 rounded p-2 whitespace-pre-wrap leading-relaxed">
                    {s.suggested_note}
                  </p>
                </details>
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-2 italic">
        Suggestions are based on objective patterns in the patient&apos;s logs. They are <strong>not clinical decisions</strong> and do not modify the prescription. The prescriber retains full clinical judgment.
      </p>
    </div>
  );
}
